import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search, UserPlus, Loader2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface InviteFriendModalProps {
  eventId: string;
  eventTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

interface UserResult {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string;
  profile_image: string | null;
}

export function InviteFriendModal({
  eventId,
  eventTitle,
  isOpen,
  onClose,
}: InviteFriendModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserResult[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [alreadyInvitedIds, setAlreadyInvitedIds] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch already invited users when modal opens
  useEffect(() => {
    if (isOpen && eventId) {
      fetchAlreadyInvited();
    }
  }, [isOpen, eventId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUsers(new Set());
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchUsers(searchQuery.trim());
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAlreadyInvited = async () => {
    try {
      const { data } = await supabase
        .from("event_invitations")
        .select("invited_user_id")
        .eq("event_id", eventId);

      if (data) {
        setAlreadyInvitedIds(new Set(data.map((inv) => inv.invited_user_id)));
      }
    } catch (error) {
      console.error("Error fetching invitations:", error);
    }
  };

  const searchUsers = async (query: string) => {
    if (!user) return;
    
    setSearching(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, user_id, first_name, last_name, business_name, profile_image")
        .neq("user_id", user.id) // Exclude current user
        .or(
          `first_name.ilike.%${query}%,last_name.ilike.%${query}%,business_name.ilike.%${query}%`
        )
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error("Error searching users:", error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const handleSendInvitations = async () => {
    if (selectedUsers.size === 0) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-event-invitation", {
        body: {
          eventId,
          invitedUserIds: Array.from(selectedUsers),
        },
      });

      if (error) throw error;

      toast({
        title: t("events.inviteFriend.invitationSent"),
        description: t("events.inviteFriend.invitationSentDesc"),
      });

      onClose();
    } catch (error: any) {
      console.error("Error sending invitations:", error);
      toast({
        title: t("common.error"),
        description: error.message || t("events.inviteFriend.invitationError"),
        variant: "error",
      });
    } finally {
      setSending(false);
    }
  };

  const getUserDisplayName = (userResult: UserResult) => {
    if (userResult.first_name || userResult.last_name) {
      return `${userResult.first_name || ""} ${userResult.last_name || ""}`.trim();
    }
    return userResult.business_name;
  };

  const filteredResults = useMemo(() => {
    return searchResults.filter((u) => !alreadyInvitedIds.has(u.user_id));
  }, [searchResults, alreadyInvitedIds]);

  const alreadyInvitedResults = useMemo(() => {
    return searchResults.filter((u) => alreadyInvitedIds.has(u.user_id));
  }, [searchResults, alreadyInvitedIds]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl">
            {t("events.inviteFriend.title")}
          </DialogTitle>
          <DialogDescription className="font-body text-sm">
            {t("events.inviteFriend.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <BrutalInput
              type="text"
              placeholder={t("events.inviteFriend.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {searching && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}

            {!searching && searchQuery.length >= 2 && filteredResults.length === 0 && alreadyInvitedResults.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-4">
                {t("events.inviteFriend.noUsersFound")}
              </p>
            )}

            {/* Available users */}
            {filteredResults.map((userResult) => {
              const isSelected = selectedUsers.has(userResult.user_id);
              return (
                <button
                  key={userResult.id}
                  type="button"
                  onClick={() => toggleUserSelection(userResult.user_id)}
                  className={`w-full flex items-center gap-3 p-3 border-2 transition-all ${
                    isSelected
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {/* Avatar */}
                  {userResult.profile_image ? (
                    <img
                      src={userResult.profile_image}
                      alt={getUserDisplayName(userResult)}
                      className="w-10 h-10 rounded-full object-cover border-2 border-dark flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center border-2 border-dark flex-shrink-0">
                      <UserPlus className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-heading font-bold truncate">
                      {getUserDisplayName(userResult)}
                    </p>
                    {userResult.business_name && userResult.first_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {userResult.business_name}
                      </p>
                    )}
                  </div>

                  {/* Checkbox */}
                  <div
                    className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? "border-primary bg-primary"
                        : "border-dark bg-background"
                    }`}
                  >
                    {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
                  </div>
                </button>
              );
            })}

            {/* Already invited users */}
            {alreadyInvitedResults.map((userResult) => (
              <div
                key={userResult.id}
                className="w-full flex items-center gap-3 p-3 border-2 border-border bg-muted/50 opacity-60"
              >
                {/* Avatar */}
                {userResult.profile_image ? (
                  <img
                    src={userResult.profile_image}
                    alt={getUserDisplayName(userResult)}
                    className="w-10 h-10 rounded-full object-cover border-2 border-dark flex-shrink-0 grayscale"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center border-2 border-dark flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 text-left min-w-0">
                  <p className="font-heading font-bold truncate text-muted-foreground">
                    {getUserDisplayName(userResult)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("events.inviteFriend.alreadyInvited")}
                  </p>
                </div>

                {/* Already invited badge */}
                <div className="w-6 h-6 border-2 border-muted-foreground flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>

          {/* Selected count and send button */}
          {selectedUsers.size > 0 && (
            <div className="pt-4 border-t border-border">
              <BrutalButton
                onClick={handleSendInvitations}
                disabled={sending}
                className="w-full"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                {t("events.inviteFriend.sendInvitation")} ({selectedUsers.size})
              </BrutalButton>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
